import { useRef } from 'react';

import { Box, Label, Shimmer, Text } from '@betterment/samba-react';
import { useFormikContext } from 'formik';

import { DeferralTypeToggle } from './DeferralTypeToggle';
import { DeferralValueField } from './DeferralValueField';

import { AccountType, DeferralSettings, FourOhOneKAccounts, PayType } from '../../types';

type RowProps = {
  payType: PayType;
  accountType: AccountType;
  label: string;
  caption: string;
  values: FourOhOneKAccounts;
  loading: boolean;
};

export const DeferralRow = ({ payType, accountType, label, caption, values, loading }: RowProps) => {
  const { setFieldValue, setValues, errors } = useFormikContext<DeferralSettings>();
  const payTypeError = errors?.[payType];

  const fieldError = payTypeError && typeof payTypeError === 'object' ? payTypeError[accountType] : undefined;
  const groupError = typeof payTypeError === 'string' ? payTypeError : undefined;
  const error = fieldError || groupError;

  // @ts-ignore We aren't calling this component for missing accounts.
  const currentType: 'amount' | 'percent' = values[accountType]?.type;
  const valueFieldName = `${payType}.${accountType}.value` as const;
  const typeFieldName = `${payType}.${accountType}.type` as const;

  const amountDraftRef = useRef(0);
  const percentDraftRef = useRef(0);

  const setFormValue = (newValue: number, unit: 'amount' | 'percent') => {
    if (unit === 'amount') {
      amountDraftRef.current = newValue;
    } else {
      percentDraftRef.current = newValue;
    }
    void setFieldValue(valueFieldName, newValue);
  };

  const handleTypeChange = (nextType: 'amount' | 'percent') => {
    const draftForNewType = nextType === 'amount' ? amountDraftRef.current : percentDraftRef.current;

    setValues((previous: DeferralSettings) => {
      const section = previous[payType];
      const slot = section![accountType];

      if (!slot) {
        return previous;
      }

      return {
        ...previous,
        [payType]: {
          ...section,
          [accountType]: {
            ...slot,
            type: nextType,
            value: draftForNewType,
          },
        },
      };
    });
  };
  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: { base: 'column', md: 'row' },
        alignItems: { base: 'flex-start', md: 'center' },
        gap: { base: 'space.100', md: 'space.150' },
        pb: accountType === 'traditional' ? 'space.300' : undefined,
      }}
    >
      <Label htmlFor={valueFieldName} css={{ flexGrow: { md: 1 } }}>
        <Text as="p">{label}</Text>
        <Text variant="caption" color="text.weak">
          {caption}
        </Text>
      </Label>

      {loading ? (
        <Shimmer css={{ height: '32px', width: '259px' }} />
      ) : (
        <Box css={{ width: { base: '100%', md: 'auto' } }}>
          <Box css={{ display: 'flex', flexDirection: 'row' }}>
            <DeferralValueField
              type={currentType}
              name={valueFieldName}
              disabled={loading}
              setFormValue={setFormValue}
            />

            <DeferralTypeToggle name={typeFieldName} disabled={loading} onTypeChange={handleTypeChange} />
          </Box>
          {error && (
            <Text variant="caption" color="text.critical" css={{ mt: 'space.50' }}>
              {typeof error === 'string' ? error : error.value}
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
};
